/* eslint-disable */
import { useQueryStates, parseAsArrayOf, parseAsString } from 'nuqs';
import { useCallback, useMemo } from 'react';

/**
 * View modes for treatment search
 */
export const TREATMENT_VIEWS = {
  STEPS: 'steps',
  RESULTS: 'results',
};

/**
 * URL parameter parsers for treatment search state
 * IMPORTANT: This hook stores ONLY IDs in the URL (no labels)
 * Labels are fetched via the bulk API by useTreatmentEnrichedData hook
 */
const createUrlParsers = () => ({
  view: parseAsString.withDefault(TREATMENT_VIEWS.STEPS),
  'treatment_cultures.culture': parseAsArrayOf(parseAsString, '|').withDefault([]),
  'treatment_cultures.cultural_practices': parseAsArrayOf(parseAsString, '|').withDefault([]),
  treatment_type: parseAsArrayOf(parseAsString, '|').withDefault([]),
  application_type: parseAsArrayOf(parseAsString, '|').withDefault([]),
  intervention_period: parseAsArrayOf(parseAsString, '|').withDefault([]),
});

/**
 * URL configuration for nuqs
 */
const URL_CONFIG = {
  history: 'push',
  shallow: false,
  throttleMs: 0,
  clearOnDefault: true,
};

/**
 * Field names for selection parameters (excludes view)
 */
const SELECTION_FIELDS = [
  'treatment_cultures.culture',
  'treatment_cultures.cultural_practices',
  'treatment_type',
  'application_type',
  'intervention_period',
];

/**
 * Hook for managing treatment search URL state with nuqs
 * Stores only IDs in URL - labels are fetched separately by useTreatmentEnrichedData
 *
 * @returns {Object} URL state and navigation functions
 */
export function useTreatmentURLState() {
  const urlParsers = useMemo(() => createUrlParsers(), []);

  // Use nuqs for URL state management
  const [urlState, setUrlState] = useQueryStates(urlParsers, URL_CONFIG);

  /**
   * Derive selections object (URL state without view)
   */
  const selections = useMemo(() => {
    const result = {};
    SELECTION_FIELDS.forEach((field) => {
      result[field] = urlState[field] || [];
    });
    return result;
  }, [urlState]);

  /**
   * Update a specific field in URL state
   * @param {string} field - Field name to update
   * @param {Array} values - Array of IDs to set
   */
  const updateField = useCallback(
    (field, values) => {
      if (SELECTION_FIELDS.includes(field)) {
        setUrlState({ [field]: values });
      }
    },
    [setUrlState]
  );

  /**
   * Navigate to results view with the provided selections
   * @param {Object} newSelections - Selection values to set (optional, uses current if not provided)
   */
  const goToResults = useCallback(
    (newSelections = null) => {
      if (newSelections) {
        setUrlState({
          ...newSelections,
          view: TREATMENT_VIEWS.RESULTS,
        });
      } else {
        setUrlState({ view: TREATMENT_VIEWS.RESULTS });
      }
    },
    [setUrlState]
  );

  /**
   * Build the absolute results URL (view=results + provided/current selections)
   * WITHOUT navigating the current tab. Used to open results in a NEW browser
   * tab — SP-430: "Les résultats s'affichent dans un nouvel onglet".
   * @param {Object} newSelections - Selection values (optional, uses current if omitted)
   * @returns {string} Absolute URL string
   */
  const buildResultsURL = useCallback(
    (newSelections = null) => {
      const sel = newSelections || selections;
      const url = new URL(window.location.href);
      url.searchParams.set('view', TREATMENT_VIEWS.RESULTS);
      SELECTION_FIELDS.forEach((field) => {
        const vals = sel[field] || [];
        if (vals.length > 0) url.searchParams.set(field, vals.join('|'));
        else url.searchParams.delete(field);
      });
      return url.toString();
    },
    [selections]
  );

  /**
   * Navigate back to steps view
   */
  const goToSteps = useCallback(() => {
    setUrlState({ view: TREATMENT_VIEWS.STEPS });
  }, [setUrlState]);

  /**
   * Reset all URL state to defaults
   */
  const resetUrlState = useCallback(() => {
    const resetState = {
      view: TREATMENT_VIEWS.STEPS,
    };
    SELECTION_FIELDS.forEach((field) => {
      resetState[field] = [];
    });
    setUrlState(resetState);
  }, [setUrlState]);

  /**
   * Check if there are valid search parameters
   * At minimum, cultures must be selected
   * @returns {boolean} True if valid search params exist
   */
  const hasValidSearchParams = useCallback(() => {
    const cultures = urlState['treatment_cultures.culture'] || [];
    return cultures.length > 0;
  }, [urlState]);

  /**
   * Get the current URL for sharing
   * @returns {string} Current window URL
   */
  const getShareableURL = useCallback(() => {
    return window.location.href;
  }, []);

  return {
    // Raw URL state
    urlState,
    setUrlState,

    // Current view
    currentView: urlState.view,

    // Derived selections (URL state without view)
    selections,

    // Field update
    updateField,

    // Navigation
    goToResults,
    goToSteps,
    buildResultsURL,

    // Reset
    resetUrlState,

    // Validation
    hasValidSearchParams,

    // Sharing
    getShareableURL,
  };
}

export default useTreatmentURLState;
